import apiClient from '../utils/axios-config';
import { Outfit } from '../types';
import { logger } from '../utils/logger';

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

class SavedOutfitsService {
  /**
   * Save an outfit to the user's saved collection.
   * POST /users/me/saved/:outfitId
   */
  async saveOutfit(outfitId: string): Promise<boolean> {
    try {
      const response = await apiClient.post(`/users/me/saved/${outfitId}`);
      return response.data?.success === true;
    } catch (error: any) {
      console.error('Error saving outfit:', error);
      logger.apiError('saving outfit', error);
      throw error;
    }
  }

  /**
   * Unsave (remove) an outfit from the user's saved collection.
   * DELETE /users/me/saved/:outfitId
   */
  async unsaveOutfit(outfitId: string): Promise<boolean> {
    try {
      const response = await apiClient.delete(`/users/me/saved/${outfitId}`);
      return response.data?.success === true;
    } catch (error: any) {
      console.error('Error unsaving outfit:', error);
      logger.apiError('unsaving outfit', error);
      throw error;
    }
  }

  /**
   * Get saved outfits for a user.
   * GET /users/:userId/saved
   */
  async getSavedOutfits(
    userId?: string,
    params?: { limit?: number; sortBy?: string }
  ): Promise<{ outfits: Outfit[]; pagination: any; userId: string }> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.append('limit', params.limit.toString());
      if (params?.sortBy) searchParams.append('sortBy', params.sortBy);

      const userIdParam = userId || 'me';
      const endpoint = `/users/${userIdParam}/saved${searchParams.toString() ? '?' + searchParams.toString() : ''}`;

      const response = await apiClient.get(endpoint);

      const responseData = response.data;

      // Handle nested data structure if present
      if (responseData && responseData.data && responseData.data.outfits) {
        return responseData.data;
      }

      return responseData;
    } catch (error: any) {
      console.error('Error fetching saved outfits:', error);
      logger.apiError('fetching saved outfits', error);
      throw error;
    }
  }

  /**
   * Check if an outfit is saved by the current user.
   * Pure function — checks against the provided savedOutfits array, no API call.
   */
  checkSaved(outfitId: string, savedOutfits: string[]): boolean {
    return savedOutfits.includes(outfitId);
  }
}

export default new SavedOutfitsService();
